import { useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Grid, Box, Typography, makeStyles, Theme, TextField, FormControl } from '@material-ui/core';
import { Edit as EditIcon } from '@material-ui/icons';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { DropDownButton, Editor, SearchBox } from '../../../components';
import { SimpleDatePicker } from '../../../components/SimpleDatePicker';
import { Entity, PartyRole } from '../../../models';
import tokens from '../../../styles/designTokens';
import { title1, title2, body2, buttonText2, body1 } from '../../../styles/typography';
import { validateDateString } from '../../../utils/dates';
import { checkNumberValue } from '../../../utils/numbers';
import { logGoogleAnalyticsEvent } from '../../../utils/sendGoogleAnalyticaEvent';
import { FinancialInfoValues, MethodTypes, TransactionTypes } from '../../NewAnalysisModal/CreateTBAModal.proptypes';
import { FinancialInfoForm } from '../../NewAnalysisModal/FinancialInfoForm';
import { TPModal } from '../../TPModal';
import { EditUncontrolledTransactionModalProps } from '../TbaUncontrolledTransactions.proptype';

const useStyles = makeStyles((theme: Theme) => ({
  modalTitleWrapper: {
    display: 'inline-flex',
    alignItems: 'center'
  },
  headerIcon: {
    color: theme.palette.primary.main,
    background: '#EFEFFF',
    borderRadius: '50%',
    boxSizing: 'content-box',
    padding: theme.spacing(0.75),
    marginRight: theme.spacing(1)
  },
  doneButton: {
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.primary.light,
    marginLeft: theme.spacing(2)
  },
  errorBorder: {
    borderRadius: '0.25rem',
    border: '1px solid red'
  },
  gridContainer: {
    backgroundColor: theme.palette.background.default,
    borderRadius: '0.25rem',
    boxShadow: '0 0 3px 0 rgba(0,0,0,0.12)',
    padding: '1.375rem 2.25rem',
    margin: '1.5rem 0',
    justifyContent: 'flex-start'
  },
  editorLabel: {
    ...body2,
    color: tokens.core1,
    margin: theme.spacing(1, 0, 0.5, 0)
  },
  FormControl: {
    width: '100%'
  },
  label: {
    ...body2,
    color: tokens.core2,
    margin: theme.spacing(1, 0, 0.5, 0)
  },
  textField: {
    width: '100%',
    '&.MuiTextField-root': {
      marginTop: 0,
      marginBottom: 0
    }
  },
  greyBackground: {
    backgroundColor: tokens.neutral70
  },
  noMargins: {
    margin: 0
  },
  transactionsList: {
    width: '100%'
  },
  placeHolder: {
    color: tokens.neutral100
  },
  title1,
  title2,
  body2,
  sectionTitle: {
    ...title1,
    color: tokens.core3
  },
  buttonGroup: {
    width: '100%'
  },
  buttonGroupToggleButton: {
    ...buttonText2,
    width: '50%',
    borderColor: tokens.core2,
    '&.Mui-selected': {
      borderColor: tokens.purpleLight2,
      '&.MuiToggleButtonGroup-groupedHorizontal:not(:last-child)': {
        borderRightColor: tokens.purpleLight2,
        marginRight: '1px'
      },
      '.MuiToggleButtonGroup-groupedHorizontal:not(:first-child)': {
        marginLeft: 0
      }
    }
  },
  dropdownStyle: {
    width: '100%',
    border: `1px solid ${tokens.neutral90}`,
    '&.MuiButton-root': {
      minWidth: 0,
      color: tokens.core2,
      justifyContent: 'flex-start',
      '&:hover': {
        backgroundColor: tokens.white
      }
    }
  },
  dropDownItem: {
    ...body1,
    color: tokens.core2
  }
}));

const ErrorMessage = ({ error, message }: { error: boolean; message: string }) =>
  error ? (
    <Typography color="error" variant="caption">
      {message}
    </Typography>
  ) : null;

const requiredTextValidation = (input: string) => input.length >= 3;

export const EditUncontrolledTransactionModal = ({
  tba,
  transactionToEdit,
  transactionToEditFiInfo,
  entities = [],
  container,
  partyRoles,
  onClose,
  submitEditedUncontrolledTransaction,
  savingEditedTransaction,
  upeCurrency,
  source
}: EditUncontrolledTransactionModalProps) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [sourceControlType, setSourceControlType] = useState(
    transactionToEdit.ucSourceLegalEntity ? 'uncontrolled' : 'controlled'
  );
  const [destinationControlType, setDestinationControlType] = useState(
    transactionToEdit.ucDestinationLegalEntity ? 'uncontrolled' : 'controlled'
  );
  const [sourceEntity, setSourceEntity] = useState<Entity | null>(
    transactionToEdit.sourceLegalEntity ?? transactionToEdit.ucSourceLegalEntity
  );
  const [destinationEntity, setDestinationEntity] = useState<Entity | null>(
    transactionToEdit.destinationLegalEntity ?? transactionToEdit.ucDestinationLegalEntity
  );
  const [sourceEntityName, setSourceEntityName] = useState<string | null>(
    transactionToEdit.ucSourceLegalEntity
      ? transactionToEdit.ucSourceLegalEntity.name
      : transactionToEdit.sourceLegalEntity?.name ?? ''
  );
  const [destinationEntityName, setDestinationEntityName] = useState<string | null>(
    transactionToEdit.ucDestinationLegalEntity
      ? transactionToEdit.ucDestinationLegalEntity.name
      : transactionToEdit.destinationLegalEntity?.name ?? ''
  );
  const [transactionId, setTransactionId] = useState(transactionToEdit.identifier);
  const [propertyTransferred, setPropertyTransferred] = useState(transactionToEdit.propertyTransferred);
  const [units, setUnits] = useState<number | null>(transactionToEdit.units);
  const [unitsErrorMessage, setUnitsErrorMessage] = useState<string>('');
  const [amount, setAmount] = useState<number | null>(transactionToEdit.value);
  const [amountErrorMessage, setAmountErrorMessage] = useState<string>('');
  const [partyRole, setPartyRole] = useState<PartyRole | null>(transactionToEdit.partyRole);
  const [description, setDescription] = useState(transactionToEdit.description);
  const [financialFormHasErrors, setFinancialFormHasErrors] = useState<boolean>(false);
  const [financialInfo, setFinancialInfo] = useState<FinancialInfoValues>({});
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const [datePickerErrorMessage, setDatePickerErrorMessage] = useState<string>(
    t('errors:error_message.transfer_date_required')
  );

  const transactionType: TransactionTypes = tba?.transactionType.name;
  const selectedMethod: MethodTypes = tba?.tbaEvaluationMethod.name;

  const currentYear = new Date().getFullYear();
  const taxYear = container?.taxYear ?? currentYear;
  const minDate = `1900-01-01`;
  const maxDate = `${taxYear + 1}-12-31`;

  const mustBeRealDate = (date: string) => {
    const { valid, message } = validateDateString({
      date,
      maxDate,
      minDate,
      invalidErrorMessage: t('errors:error_message.invalid_date'),
      lessThanErrorMessage: t('errors:error_message.date_can_not_be_less_than'),
      greaterThanErrorMessage: t('errors:error_message.date_can_not_be_larger_than')
    });

    setDatePickerErrorMessage(date ? message : t('errors:error_message.transfer_date_required'));

    return valid;
  };

  const [transferDate, setTransferDate] = useState<string | null>(transactionToEdit.transferDate ?? '');

  const defaultValues = {
    sourceEntity,
    destinationEntity,
    sourceEntityName,
    destinationEntityName,
    transactionId,
    propertyTransferred,
    transferDate,
    units: typeof units === 'number' ? String(units) : units,
    amount: typeof amount === 'number' ? String(amount) : amount,
    partyRole,
    description
  };
  const formMethods = useForm({
    defaultValues
  });
  const { handleSubmit, control, errors, setValue } = formMethods;

  const unitsAreRequired =
    (tba?.tbaEvaluationMethod.tbaEvaluationMethodId === 3 && tba?.transactionType.transactionTypeId === 3) ||
    (tba?.tbaEvaluationMethod.tbaEvaluationMethodId === 3 && tba?.transactionType.transactionTypeId === 1) ||
    (tba?.tbaEvaluationMethod.tbaEvaluationMethodId === 1 && tba?.transactionType.transactionTypeId === 1);

  const validateUnits = (str: string) => {
    switch (checkNumberValue(str)) {
      case 'empty':
        setUnitsErrorMessage(t('errors:error_message.amount_required'));
        return !unitsAreRequired;
      case 'not a number':
      case 'negative number':
        setUnitsErrorMessage(t('errors:error_message.negative_numbers_not_allowed'));
        return false;
      case 'zero':
      case 'positive number':
      default:
        return true;
    }
  };

  const validateAmount = (str: string) => {
    switch (checkNumberValue(str)) {
      case 'empty':
        setAmountErrorMessage(t('errors:error_message.amount_required'));
        return false;
      case 'not a number':
      case 'negative number':
        setAmountErrorMessage(t('errors:error_message.negative_numbers_not_allowed'));
        return false;
      case 'zero':
      case 'positive number':
      default:
        return true;
    }
  };

  const submitButtonDisabled = savingEditedTransaction;

  const setEntityControlType = (which: 'source' | 'destination', controlType: 'controlled' | 'uncontrolled') => {
    if (which === 'source' && sourceControlType !== controlType) {
      setSourceControlType(controlType);
      setSourceEntity(null);
      setSourceEntityName(null);
      setValue('sourceEntity', null);
      setValue('sourceEntityName', null);
      if (controlType === 'controlled' && destinationControlType === 'controlled') {
        setDestinationControlType('uncontrolled');
        setDestinationEntity(null);
        setDestinationEntityName(null);
        setValue('destinationEntity', null);
        setValue('destinationEntityName', null);
      }
    } else if (which === 'destination' && destinationControlType !== controlType) {
      setDestinationControlType(controlType);
      setDestinationEntity(null);
      setDestinationEntityName(null);
      setValue('destinationEntity', null);
      setValue('destinationEntityName', null);
      if (controlType === 'controlled' && sourceControlType === 'controlled') {
        setSourceControlType('uncontrolled');
        setSourceEntity(null);
        setSourceEntityName(null);
        setValue('sourceEntity', null);
        setValue('sourceEntityName', null);
      }
    }
  };

  const sourceEntityInput = useMemo(
    () =>
      sourceControlType === 'uncontrolled' ? (
        <Box>
          <Typography className={classes.label}>{t('analysis:column-source-entity')}</Typography>
          <Controller
            name="sourceEntityName"
            render={({ onChange }) => (
              <TextField
                id="sourceEntityName"
                name="sourceEntityName"
                placeholder={t('entities:placeholder-source-name')}
                className={classes.textField}
                margin="dense"
                variant="outlined"
                value={sourceEntityName}
                inputProps={{ 'data-testid': 'sourceEntityName' }}
                error={Boolean(showErrors && !sourceEntityName)}
                onChange={(item) => {
                  setSourceEntityName(typeof item?.target?.value === 'string' ? item.target.value : '');
                  onChange(item);
                }}
              />
            )}
            control={control}
            defaultValue={{}}
            rules={{ required: true }}
            error={Boolean(showErrors && !sourceEntityName)}
          />
          <ErrorMessage
            error={Boolean(showErrors && !sourceEntityName)}
            message={t('transactions:invalid-entry-required-source-name')}
          />
        </Box>
      ) : (
        <Box>
          <Typography className={classes.label}>{t('analysis:column-source-entity')}</Typography>
          <FormControl size="small" variant="outlined" className={classes.FormControl}>
            <Controller
              name="sourceEntity"
              render={({ onChange }) => (
                <SearchBox
                  placeholder={t('entities:placeholder-source-name')}
                  error={Boolean(showErrors && !sourceEntity)}
                  value={sourceEntity}
                  options={entities.filter((source: any) => destinationEntity?.entityId !== source.entityId)}
                  getOptionSelected={(option, value) => option.code === value.code}
                  getOptionLabel={(source) => source.code ?? ''}
                  onChange={(_, item) => {
                    setSourceEntity(item);
                    setSourceEntityName(item?.name ?? null);
                    onChange(item);
                  }}
                />
              )}
              control={control}
              rules={{ required: true }}
              defaultValue={{}}
              error={Boolean(showErrors && !sourceEntity)}
            />
          </FormControl>
          <ErrorMessage
            error={Boolean(showErrors && !sourceEntity)}
            message={t('transactions:invalid-entry-required-source')}
          />
        </Box>
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sourceEntity, sourceEntityName, sourceControlType, destinationControlType, showErrors]
  );

  const destinationEntityInput = useMemo(
    () =>
      destinationControlType === 'uncontrolled' ? (
        <Box>
          <Typography className={classes.label}>{t('entities:label-copy-destination')}</Typography>
          <Controller
            name="destinationEntityName"
            render={({ onChange }) => (
              <TextField
                id="destinationEntityName"
                name="destinationEntityName"
                placeholder={t('entities:placeholder-destination-name')}
                className={classes.textField}
                margin="dense"
                variant="outlined"
                value={destinationEntityName}
                inputProps={{ 'data-testid': 'destinationEntityName' }}
                error={Boolean(showErrors && !destinationEntityName)}
                onChange={(item) => {
                  setDestinationEntityName(typeof item?.target?.value === 'string' ? item.target.value : '');
                  onChange(item);
                }}
              />
            )}
            control={control}
            defaultValue={{}}
            rules={{ required: true }}
            error={Boolean(showErrors && !destinationEntityName)}
          />
          <ErrorMessage
            error={Boolean(showErrors && !destinationEntityName)}
            message={t('transactions:invalid-entry-required-destination-name')}
          />
        </Box>
      ) : (
        <Box>
          <Typography className={classes.label}>{t('entities:label-copy-destination')}</Typography>
          <Controller
            name="destinationEntity"
            render={({ onChange }) => (
              <SearchBox
                placeholder={t('entities:placeholder-destination-name')}
                error={Boolean(showErrors && !destinationEntity)}
                value={destinationEntity}
                options={entities.filter((destination: Entity) => destinationEntity?.entityId !== destination.entityId)}
                getOptionSelected={(option, value) => option.code === value.code}
                getOptionLabel={(destination) => destination.code ?? ''}
                onChange={(_, item) => {
                  setDestinationEntity(item);
                  setDestinationEntityName(item?.name ?? null);
                  onChange(item);
                }}
              />
            )}
            control={control}
            rules={{ required: true }}
            defaultValue={{}}
            error={Boolean(showErrors && !destinationEntity)}
          />
          <ErrorMessage
            error={Boolean(showErrors && !destinationEntity)}
            message={t('transactions:invalid-entry-required-destination')}
          />
        </Box>
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [destinationEntity, destinationEntityName, destinationControlType, sourceControlType, showErrors]
  );

  return (
    <TPModal
      isOpen
      maxWidth="md"
      title={
        <span className={classes.modalTitleWrapper}>
          <EditIcon className={classes.headerIcon} />
          <Typography className={classes.title2}>{t('analysis:edit-uncontrolled-transaction')}</Typography>
        </span>
      }
      containerStyle={classes.greyBackground}
      actions={[
        {
          name: t('action-cancel'),
          handler: () => {
            /* eslint-disable camelcase */
            void logGoogleAnalyticsEvent({
              event_category: 'tba_button_click',
              event_label: `${source} On Cancel Edit Single Transaction Modal click`,
              container_id: container?.containerId
            });
            onClose();
          }
        },
        {
          name: t('action-done'),
          className: classes.doneButton,
          disabled: submitButtonDisabled,
          handler: async () => {
            setShowErrors(true);
            return handleSubmit(() => {
              /* eslint-disable camelcase */
              void logGoogleAnalyticsEvent({
                event_category: 'tba_button_click',
                event_label: `${source} On Save Edit Single Transaction Modal click`,
                container_id: container?.containerId
              });
              const data = {
                amount,
                description,
                destinationEntity: destinationControlType === 'controlled' ? destinationEntity : null,
                financialInfo,
                identifier: transactionId,
                partyRole,
                propertyTransferred,
                selectedMethod,
                sourceEntity: sourceControlType === 'controlled' ? sourceEntity : null,
                transactionId: transactionToEdit.transactionId,
                transferDate,
                transactionType,
                ucSourceLegalEntity: sourceControlType === 'controlled' ? null : { name: sourceEntityName },
                ucDestinationLegalEntity:
                  destinationControlType === 'controlled' ? null : { name: destinationEntityName },
                units
              };
              if (!financialFormHasErrors) {
                submitEditedUncontrolledTransaction(data);
              }
            })();
          }
        }
      ]}
      onClose={() => {
        /* eslint-disable camelcase */
        void logGoogleAnalyticsEvent({
          event_category: 'tba_button_click',
          event_label: `${source} On Close Edit Single Transaction Modal click`,
          container_id: container?.containerId
        });
        onClose();
      }}
    >
      <Grid container justify="center" className={classes.noMargins}>
        <Grid container item xs={12} justify="center">
          <Grid container item xs={12} spacing={1} justify="space-around" className={classes.gridContainer}>
            <Grid item xs={6}>
              <Box>
                <Typography className={classes.sectionTitle}>{t('transactions:column-source')}</Typography>
                <Typography className={classes.label}>{t('entities:label-describe-source-entity')}</Typography>
                <ToggleButtonGroup exclusive className={classes.buttonGroup} size="small" value={sourceControlType}>
                  <ToggleButton
                    className={classes.buttonGroupToggleButton}
                    value="uncontrolled"
                    onClick={() => {
                      setEntityControlType('source', 'uncontrolled');
                    }}
                  >
                    <Typography>{t('entities:label-uncontrolled')}</Typography>
                  </ToggleButton>
                  <ToggleButton
                    className={classes.buttonGroupToggleButton}
                    value="controlled"
                    onClick={() => {
                      setEntityControlType('source', 'controlled');
                    }}
                  >
                    <Typography>{t('entities:label-controlled')}</Typography>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {sourceEntityInput}
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Typography className={classes.sectionTitle}>{t('entities:label-copy-destination')}</Typography>
                <Typography className={classes.label}>{t('entities:label-describe-destination-entity')}</Typography>
                <ToggleButtonGroup
                  exclusive
                  className={classes.buttonGroup}
                  size="small"
                  value={destinationControlType}
                >
                  <ToggleButton
                    className={classes.buttonGroupToggleButton}
                    value="uncontrolled"
                    onClick={() => {
                      setEntityControlType('destination', 'uncontrolled');
                    }}
                  >
                    <Typography>{t('entities:label-uncontrolled')}</Typography>
                  </ToggleButton>
                  <ToggleButton
                    className={classes.buttonGroupToggleButton}
                    value="controlled"
                    onClick={() => {
                      setEntityControlType('destination', 'controlled');
                    }}
                  >
                    <Typography>{t('entities:label-controlled')}</Typography>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {destinationEntityInput}
            </Grid>
          </Grid>
          <Grid container item xs={12} spacing={1} justify="space-around" className={classes.gridContainer}>
            <Grid item xs={12}>
              <Box>
                <Typography className={classes.sectionTitle}>{t('transactions:title-details')}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Typography className={classes.label}>{t('transactions:column-id')}</Typography>
                <FormControl size="small" variant="outlined" className={classes.FormControl}>
                  <Controller
                    name="transactionId"
                    render={({ value, onChange }) => (
                      <TextField
                        id="transactionId"
                        name="transactionId"
                        placeholder={t('entities:placeholder-transaction-id')}
                        className={classes.textField}
                        margin="dense"
                        variant="outlined"
                        value={value}
                        inputProps={{ 'data-testid': 'transactionId' }}
                        error={Boolean(errors.transactionId)}
                        onChange={(item) => {
                          setTransactionId(typeof item?.target?.value === 'string' ? item.target.value : '');
                          onChange(item);
                        }}
                      />
                    )}
                    control={control}
                    defaultValue={defaultValues.transactionId}
                    rules={{ required: true, validate: requiredTextValidation }}
                    error={Boolean(errors.transactionId)}
                  />
                </FormControl>
              </Box>
              <ErrorMessage
                error={Boolean(errors.transactionId)}
                message={t('errors:error_message.transaction_id_required')}
              />
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Typography className={classes.label}>{t('analysis:column-transaction-type')}</Typography>
                <TextField
                  disabled
                  id="transactionType"
                  name="transactionType"
                  placeholder={t('entities:placeholder-transaction-id')}
                  className={classes.textField}
                  margin="dense"
                  variant="outlined"
                  value={transactionType}
                  inputProps={{ 'data-testid': 'transactionType' }}
                />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Typography className={classes.label}>{t('transactions:label-property-transferred')}</Typography>
                <Controller
                  name="propertyTransferred"
                  render={({ value, onChange }) => (
                    <TextField
                      id="propertyTransferred"
                      name="propertyTransferred"
                      placeholder={t('entities:placeholder-property-transferred')}
                      className={classes.textField}
                      margin="dense"
                      variant="outlined"
                      value={value}
                      inputProps={{ 'data-testid': 'propertyTransferred' }}
                      error={Boolean(errors.propertyTransferred)}
                      onChange={(item) => {
                        setPropertyTransferred(typeof item?.target?.value === 'string' ? item.target.value : '');
                        onChange(item);
                      }}
                    />
                  )}
                  control={control}
                  defaultValue={defaultValues.propertyTransferred}
                  rules={{ required: true }}
                  error={Boolean(errors.propertyTransferred)}
                />
              </Box>
              <ErrorMessage
                error={Boolean(errors.propertyTransferred)}
                message={t('errors:error_message.property_transferred_required')}
              />
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Typography className={classes.label}>{t('transactions:column-transfer')}</Typography>
                <Controller
                  name="transferDate"
                  render={({ onChange, value }) => (
                    <SimpleDatePicker
                      defaultDate={value ?? defaultValues.transferDate ?? ''}
                      onChange={(newDate) => {
                        onChange(newDate);
                        setTransferDate(newDate);
                      }}
                    />
                  )}
                  control={control}
                  defaultValue={defaultValues.transferDate}
                  rules={{ validate: mustBeRealDate }}
                  error={Boolean(errors.transferDate)}
                />
              </Box>
              <ErrorMessage error={Boolean(errors.transferDate)} message={datePickerErrorMessage} />
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Typography className={classes.label}>{t('transactions:label-units')}</Typography>
                <Controller
                  name="units"
                  render={({ value, onChange }) => (
                    <TextField
                      id="units"
                      name="units"
                      placeholder={t('entities:placeholder-units')}
                      className={classes.textField}
                      margin="dense"
                      variant="outlined"
                      value={value}
                      inputProps={{ 'data-testid': 'units' }}
                      error={Boolean(errors.units)}
                      onChange={(item) => {
                        setUnits(
                          checkNumberValue(item.target.value) === 'positive number' ||
                            checkNumberValue(item.target.value) === 'zero'
                            ? Number(item.target.value)
                            : null
                        );
                        onChange(item);
                      }}
                    />
                  )}
                  control={control}
                  defaultValue={defaultValues.units}
                  rules={{ validate: validateUnits }}
                  error={Boolean(errors.units)}
                />
              </Box>
              <ErrorMessage error={Boolean(errors.units)} message={unitsErrorMessage} />
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Typography className={classes.label}>{t('transactions:label-input-currency')}</Typography>
                <TextField
                  disabled
                  id="inputCurrency"
                  name="inputCurrency"
                  placeholder={t('entities:placeholder-transaction-id')}
                  className={classes.textField}
                  margin="dense"
                  variant="outlined"
                  value={upeCurrency.name}
                  inputProps={{ 'data-testid': 'inputCurrency' }}
                />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Typography className={classes.label}>
                  {t('transactions:label-value', { currency: upeCurrency.isoCode })}
                </Typography>
                <Controller
                  name="amount"
                  render={({ value, onChange }) => (
                    <TextField
                      id="amount"
                      name="amount"
                      placeholder={t('entities:placeholder-amount')}
                      className={classes.textField}
                      margin="dense"
                      variant="outlined"
                      value={value}
                      inputProps={{ 'data-testid': 'amount' }}
                      error={Boolean(errors.amount)}
                      onChange={(item) => {
                        setAmount(
                          checkNumberValue(item.target.value) === 'positive number' ||
                            checkNumberValue(item.target.value) === 'zero'
                            ? Number(item.target.value)
                            : null
                        );
                        onChange(item);
                      }}
                    />
                  )}
                  control={control}
                  defaultValue={defaultValues.amount}
                  rules={{ validate: validateAmount }}
                  error={Boolean(errors.amount)}
                />
              </Box>
              <ErrorMessage error={Boolean(errors.amount)} message={amountErrorMessage} />
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Typography className={classes.label}>{t('transactions:label-party-roles')}</Typography>
                <Controller
                  name="partyRole"
                  render={({ onChange }) => (
                    <DropDownButton
                      variant="text"
                      className={classes.dropdownStyle}
                      items={partyRoles?.map((partyRole: PartyRole) => {
                        return {
                          children: (
                            <Typography className={classes.dropDownItem}>
                              {t(`transactions:party-role-${partyRole.name}`)}
                            </Typography>
                          ),
                          onClick: () => {
                            setPartyRole(partyRole);
                            onChange(partyRole);
                          }
                        };
                      })}
                    >
                      {partyRole ? t(`transactions:party-role-${partyRole.name}`) : ''}
                    </DropDownButton>
                  )}
                  control={control}
                  defaultValue={defaultValues.partyRole}
                  rules={{ required: true }}
                  error={Boolean(errors.partyRole)}
                />
              </Box>
              <ErrorMessage error={Boolean(errors.partyRole)} message={t('errors:error_message.party_role_required')} />
            </Grid>
            <Grid item xs={12}>
              <Box>
                <Typography className={classes.label}>{t('analysis:description')}</Typography>
                <Controller
                  name="description"
                  render={({ value, onChange }) => (
                    <Editor
                      theme="TransferPricing"
                      value={value}
                      onEditorChange={(content) => {
                        onChange(content);
                        setDescription(content ?? '');
                      }}
                    />
                  )}
                  control={control}
                  defaultValue={defaultValues.description}
                  rules={{ required: true }}
                  error={Boolean(errors.description)}
                />
              </Box>
              <ErrorMessage
                error={Boolean(errors.description)}
                message={t('errors:error_message.description_required')}
              />
            </Grid>
          </Grid>
          {partyRoles && container && (
            <FinancialInfoForm
              originalFinancialInfo={transactionToEditFiInfo}
              selectedMethod={selectedMethod}
              selectedTransactionType={transactionType}
              selectedTransactions={[
                {
                  index: 1,
                  id: String(transactionToEdit.transactionId),
                  transactionId: transactionToEdit.transactionId
                }
              ]}
              transactions={[
                {
                  transactionId: transactionToEdit.transactionId,
                  units,
                  value: Number(amount),
                  tbas: [{ tbaId: tba.tbaId, sourceCompleted: false, destinationCompleted: false }],
                  transactionType: tba?.transactionType,
                  propertyTransferred,
                  partyRole: partyRoles[0],
                  description,
                  transferDate: '2021-01-01',
                  valueEnteredIn: 1,
                  container,
                  identifier: transactionId,
                  legalEntityTransactions: [],
                  pbas: [{ pbaId: 2, sourceCompleted: false, destinationCompleted: false }]
                }
              ]}
              setFinancialFormHasErrors={setFinancialFormHasErrors}
              setFinancialInfo={setFinancialInfo}
              showErrors={showErrors}
            />
          )}
        </Grid>
      </Grid>
    </TPModal>
  );
};
